import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandEmpty, CommandGroup, CommandInput,
  CommandItem, CommandList, CommandSeparator,
} from "@/components/ui/command";
import { Plus } from "lucide-react";

/**
 * Global Command Palette (Cmd+K / Ctrl+K e a lupa do celular).
 * Navegação por busca + ações rápidas. Registrado uma única vez no AppShell.
 *
 * As telas vêm do menu (`navGroups`, já filtrado por papel, permissão e recurso
 * no AppShell). A busca tinha uma lista própria e ficava para trás: tela nova no
 * menu (Impostos) não aparecia na busca.
 */
const SHORTCUTS = { "/": "G D", "/pos": "G V", "/customers": "G C", "/products": "G P", "/reports": "G R", "/billing": "G B" };

const QUICK_ACTIONS = [
  { id: "qa-new-sale",     label: "Nova venda",     icon: Plus, to: "/pos",       keywords: "nova venda criar" },
  { id: "qa-new-customer", label: "Novo cliente",   icon: Plus, to: "/customers?new=1", keywords: "cadastrar cliente" },
  { id: "qa-new-product",  label: "Novo produto",   icon: Plus, to: "/products?new=1",  keywords: "cadastrar produto" },
  { id: "qa-new-payable",  label: "Nova conta a pagar", icon: Plus, to: "/financial-central?tab=payables", keywords: "conta pagar boleto" },
  { id: "qa-new-opcost",   label: "Lançar custo/despesa", icon: Plus, to: "/opcosts?new=1", keywords: "custo despesa folha aluguel" },
];

export default function CommandPalette({ open, onOpenChange, navGroups = [] }) {
  const nav = useNavigate();

  const go = (to) => { onOpenChange(false); nav(to); };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Buscar tela, ação ou atalho…" data-testid="cmdk-input" />
      <CommandList>
        <CommandEmpty>Nenhum resultado.</CommandEmpty>
        <CommandGroup heading="Ações rápidas">
          {QUICK_ACTIONS.map(a => (
            <CommandItem key={a.id} value={`${a.label} ${a.keywords || ""}`} onSelect={() => go(a.to)} data-testid={`cmdk-${a.id}`}>
              <a.icon className="w-4 h-4 mr-2" />{a.label}
            </CommandItem>
          ))}
        </CommandGroup>
        <CommandSeparator />
        {navGroups.map(g => (
          <CommandGroup key={g.id} heading={g.label}>
            {g.visibleItems.map(i => (
              <CommandItem key={i.to} value={`${i.label} ${g.label} ${i.keywords || ""}`} onSelect={() => go(i.to)}
                           data-testid={`cmdk-nav-${i.to.replace(/[^a-z0-9]+/gi, "-")}`}>
                <i.icon className="w-4 h-4 mr-2" />{i.label}
                {SHORTCUTS[i.to] && <span className="ml-auto text-[10px] text-neutral-400">{SHORTCUTS[i.to]}</span>}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
}

/**
 * useGlobalHotkeys — registers keyboard shortcuts globally.
 *  • Cmd/Ctrl+K   → toggle palette
 *  • /            → focus [data-search] input on page (if present)
 *  • G D/V/C/P/R/B → navigate (Gmail-style)
 *  • N            → click [data-primary-action] button on page (if present)
 *  • Esc          → close palette
 */
export function useGlobalHotkeys(setPaletteOpen) {
  const nav = useNavigate();
  useEffect(() => {
    let goMode = false;
    let goTimer = null;

    const isEditable = (el) => {
      if (!el) return false;
      const tag = el.tagName?.toLowerCase();
      return tag === "input" || tag === "textarea" || tag === "select" || el.isContentEditable;
    };

    const handler = (e) => {
      // ⌘K / Ctrl+K
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setPaletteOpen(o => !o);
        return;
      }
      if (isEditable(document.activeElement)) return;

      // "/" focuses search
      if (e.key === "/") {
        const el = document.querySelector('[data-search], input[type="search"]');
        if (el) { e.preventDefault(); el.focus(); }
        return;
      }
      // "N" clicks primary action
      if (e.key === "n" || e.key === "N") {
        const btn = document.querySelector('[data-primary-action]');
        if (btn) { e.preventDefault(); btn.click(); }
        return;
      }
      // Esc closes palette (handled by CommandDialog)
      // G + x navigation
      if (e.key === "g" || e.key === "G") {
        goMode = true;
        clearTimeout(goTimer);
        goTimer = setTimeout(() => { goMode = false; }, 800);
        return;
      }
      if (goMode) {
        goMode = false; clearTimeout(goTimer);
        const map = {
          d: "/", D: "/",
          v: "/pos", V: "/pos",
          c: "/customers", C: "/customers",
          p: "/products", P: "/products",
          r: "/reports", R: "/reports",
          b: "/billing", B: "/billing",
        };
        if (map[e.key]) { e.preventDefault(); nav(map[e.key]); }
      }
    };

    window.addEventListener("keydown", handler);
    return () => { window.removeEventListener("keydown", handler); clearTimeout(goTimer); };
  }, [nav, setPaletteOpen]);
}
